import type {
  QuestionnaireDraft,
  QuestionnaireValidateResponse,
  QuestionnaireValidationIssue,
} from "@digital-twins/shared-types";

import { indexBlockingIssuesByQuestionId } from "./indexValidationIssues";

/**
 * What the server's dry run of the unsaved Save (`POST …/validate`, T1b: one
 * validity verdict) says on each question of the draft on screen, by question
 * id: the issues the Save's result would still have (its readiness), and what
 * the Save would change without refusing (its effects, a `warning` carrying
 * the effect kind, said in the card's notice). Its refusal is the Save's own
 * refusal and is mapped by the Save's mapper. No rule runs here.
 */
export type DryRunIssues = ReadonlyMap<
  string,
  ReadonlyArray<QuestionnaireValidationIssue>
>;

export const NO_DRY_RUN_ISSUES: DryRunIssues = new Map();

/** The Save's effect kinds a card says in its notice. */
export const LIVE_EFFECT_KINDS: ReadonlySet<string> = new Set([
  "asset_ref_removed",
  "asset_choice_removed",
  "asset_row_removed",
  "condition_value_pruned",
]);

/** Whether an issue is what a Save would change, not a refusal. */
export const isLiveEffect = (issue: QuestionnaireValidationIssue): boolean =>
  issue.severity === "warning" && LIVE_EFFECT_KINDS.has(issue.code);

export function dryRunIssuesByQuestionId(
  result: QuestionnaireValidateResponse,
  local: QuestionnaireDraft,
): DryRunIssues {
  const byId = new Map<string, Array<QuestionnaireValidationIssue>>();
  const add = (id: string, issue: QuestionnaireValidationIssue) =>
    byId.set(id, [...(byId.get(id) ?? []), issue]);
  if (result.readiness) {
    indexBlockingIssuesByQuestionId(
      result.readiness.blockingIssues,
      local,
    ).byQuestionId.forEach((issues, id) => issues.forEach((issue) => add(id, issue)));
  }
  const seen = new Set<string>();
  for (const { kind, questionStableKey, line } of result.effects) {
    if (!LIVE_EFFECT_KINDS.has(kind) || !questionStableKey || !line) continue;
    const index = local.researchQuestions.findIndex(
      (question) => question.stableKey === questionStableKey,
    );
    const id = local.researchQuestions[index]?.id;
    if (!id || seen.has(`${id}:${kind}`)) continue;
    seen.add(`${id}:${kind}`);
    add(id, {
      code: kind,
      path: `researchQuestions[${index}]`,
      message: line,
      severity: "warning",
    } as QuestionnaireValidationIssue);
  }
  return byId.size === 0 ? NO_DRY_RUN_ISSUES : byId;
}
